import { Component, ElementRef, ViewChild, ChangeDetectorRef, OnDestroy } from '@angular/core'
import { Router } from '@angular/router'
import { listOfStates } from '../../data/states'
import { NgClass } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { FloatLabelModule } from 'primeng/floatlabel'
import { InputTextModule } from 'primeng/inputtext'
import { SelectModule } from 'primeng/select'
import { DialogModule } from 'primeng/dialog'
import { ButtonModule } from 'primeng/button'
import { RaApi } from '../../services/ra-api/ra-api'
import { OfferRequest } from '../../services/ra-api/models/offer-models'
import { GlobalVariable } from '../../services/global-variable/global-variable'
import { CallUsButton } from '../call-us-button/call-us-button'
import { orangeDarkBg } from '../../../assets/designTokens/customTokens'
import { Subscription } from 'rxjs'

interface Tab {
	label: string
	hasSupportingInfo: boolean
}

@Component({
	selector: 'app-offer-form',
	imports: [
		FloatLabelModule,
		InputTextModule,
		FormsModule,
		SelectModule,
		DialogModule,
		ButtonModule,
		NgClass,
		CallUsButton,
	],
	templateUrl: './offer-form.html',
	styleUrl: './offer-form.css',
})
export class OfferForm implements OnDestroy {
	orangeDarkBg = orangeDarkBg

	@ViewChild('list') list!: ElementRef
	@ViewChild('zipInput') zipInputRef!: ElementRef
	@ViewChild('licensePlateInput') licensePlateInputRef!: ElementRef
	@ViewChild('stateInput') stateInputRef!: ElementRef
	@ViewChild('errorDiv') errorDivRef!: ElementRef
	@ViewChild('errorMessage') errorMessageRef!: ElementRef

	errorMessage: string = ''
	defaultNotServiceableDialog: boolean = false
	isLpSearch: boolean = true
	isVinSearch: boolean = false
	isStateInvalid: boolean = false
	vinHelpIsVisible: boolean = false
	isSubmitting: boolean = false

	active: number = 0
	prevRect: { left: number; width: number } = { left: 0, width: 0 }

	licensePlateNumber: string | undefined
	vin: string | undefined
	zip: string | undefined
	states = listOfStates

	selectedState: any

	tabs: Tab[] = [
		{ label: 'LICENSE PLATE', hasSupportingInfo: false },
		{ label: 'VIN', hasSupportingInfo: true },
	]

	private offerSubscription!: Subscription;

	constructor(
		private raApi: RaApi,
		private router: Router,
		private globalVariable: GlobalVariable,
		private cdr: ChangeDetectorRef,
	) {} // Router injected for navigation to /smc wizard/instant-offer

	ngOnDestroy(): void {
		this.cdr?.detach()
		this.offerSubscription?.unsubscribe()
	}

	setActiveTab = (event: MouseEvent, index: number) => {
		if (this.active === index) return

		this.active = index

		// clear fields on tab switch
		if (this.tabs[index].label === 'VIN') {
			this.licensePlateNumber = ''
			this.selectedState = null
			this.vin = ''
			this.isLpSearch = false
			this.isVinSearch = true
		} else if (this.tabs[index].label === 'LICENSE PLATE') {
			this.licensePlateNumber = ''
			this.selectedState = null
			this.vin = ''
			this.isLpSearch = true
			this.isVinSearch = false
		}

		// Indicator Animation
		const tab = (event.currentTarget as HTMLElement).parentElement as HTMLElement
		const newRect = tab.getBoundingClientRect()

		const dx = this.prevRect.left - newRect.left
		const dw = this.prevRect.width / newRect.width

		this.list.nativeElement.style.setProperty('--tab-dx', `${dx}px`)
		this.list.nativeElement.style.setProperty('--tab-dw', dw)

		// Next frame
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				this.list.nativeElement.style.setProperty('--tab-dx', '0px')
				this.list.nativeElement.style.setProperty('--tab-dw', 1)
			})
		})

		tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })

		this.prevRect = newRect
	}

	submitOfferForm(): void {
		// disable button & show spinner
		this.isSubmitting = true

		if (this.isLpSearch) {
			if (
				this.licensePlateNumber === '' ||
				this.licensePlateNumber === undefined ||
				this.selectedState === '' ||
				this.selectedState === null ||
				this.selectedState === undefined ||
				this.zip === '' ||
				this.zip === undefined
			) {
				this.showMissingFieldsError(true, 'Please complete all fields')
				this.isSubmitting = false
				return
			}

			if (this.licensePlateNumber === '' || this.licensePlateNumber === undefined || this.licensePlateNumber === null) {
				this.validateLicensePlateInput(true, 'Please enter a license plate')
				this.isSubmitting = false
				return
			}

			if (this.selectedState === null || this.selectedState === '' || this.selectedState === undefined) {
				this.isStateInvalid = true
				this.validateStateInput(true, 'Please choose a state')
				this.isSubmitting = false
				return
			}

			const alphaNumericRegex = /^[a-zA-Z0-9]*$/
			if (this.licensePlateNumber && !alphaNumericRegex.test(this.licensePlateNumber as string)) {
				let message = 'License Plate must be valid.'
				this.validateLicensePlateInput(true, message)
				this.isSubmitting = false
				return
			}
		}

		// US ZIP validation: must be 5 digits optionally with -4 format
		const zipPattern = /^\d{5}(-\d{4})?$/
		if (this.zip === null || this.zip === undefined || this.zip === '' || !zipPattern.test(this.zip as string)) {
			this.validateZipInput(true, 'Please use a valid five digit zip code.')
			this.isSubmitting = false
			return
		} else {
			if (this.zipInputRef) {
				this.globalVariable.zipCode = this.zip
				this.validateZipInput(false, '')
			}
		}

		const offerRequest: OfferRequest = {
			vehicle: {
				vin: this.vin ?? '',
				license_number: this.licensePlateNumber ?? '',
				license_state: this.selectedState?.value ?? '',
				ownership_status: '',
				title_status: '',
				damage: [],
			},
			address_seller: {
				name_first: '',
				name_last: '',
				email: '',
				address1: '',
				address2: '',
				city: '',
				state: '',
				zip: '',
				phone: '',
			},
			address_pickup: {
				address1: '',
				address2: '',
				city: '',
				state: '',
				zip: this.zip ?? '',
				pickup_datetime: '',
			},
		}

		this.offerSubscription = this.raApi.processVehicle(offerRequest).subscribe({
			next: (response) => {
				if (response?.response_code === 'CONTINUE') {
					this.router.navigate(['/instant-offer'], { queryParams: { offer_id: response?.offer_id } })
					this.globalVariable.offerId = response?.offer_id
				}

				// Invalid License Plate
				if (response?.response_code === 'INVALID_LICENSE') {
					this.validateLicensePlateInput(
						true,
						`We couldn't find that license plate. Please try again or search with your VIN.`,
					)
					this.isSubmitting = false
				}

				// Invalid Zip API response
				if (response?.response_code === 'INVALID_PICKUP_ZIP') {
					this.validateZipInput(true, 'Please use a valid five digit zip code.')
					this.isSubmitting = false
				}

				// Zip Code not serviceable or LP did not have any data
				if (response?.response_code === 'INVALID_NOT_SERVICEABLE' || response?.response_code === 'INVALID_PLATE_RUN') {
					this.defaultNotServiceableDialog = true
					this.isSubmitting = false
				}

				if (response?.response_code === 'INVALID_VIN') {
					this.defaultNotServiceableDialog = true
					this.isSubmitting = false
				}

				if (response?.response_code === 'INVALID_VIN_PRICE') {
					this.defaultNotServiceableDialog = true
					this.isSubmitting = false
				}

				this.cdr.markForCheck()
			},
			error: (err) => {
				console.error('Error submitting offer form', err)
				this.defaultNotServiceableDialog = true
				this.isSubmitting = false
				this.cdr.markForCheck()
			},
		})
	}

	showMissingFieldsError(error: boolean, message: string) {
		if (error) {
			this.errorDivRef.nativeElement.style.display = 'block'
		} else {
			this.errorDivRef.nativeElement.style.display = 'none'
		}
		this.errorMessageRef.nativeElement.innerText = message
	}

	validateZipInput(error: boolean, message: string) {
		if (error) {
			this.zipInputRef.nativeElement.classList.add('zip-invalid')
			this.zipInputRef.nativeElement.classList.remove('ng-valid')
			this.errorDivRef.nativeElement.style.display = 'block'
		} else {
			this.zipInputRef.nativeElement.classList.remove('zip-invalid')
			this.zipInputRef.nativeElement.classList.add('ng-valid')
			this.errorDivRef.nativeElement.style.display = 'none'
		}
		this.errorMessageRef.nativeElement.innerText = message
	}

	validateLicensePlateInput(error: boolean, message: string) {
		if (error) {
			this.licensePlateInputRef.nativeElement.classList.add('zip-invalid')
			this.licensePlateInputRef.nativeElement.classList.remove('ng-valid')
			this.errorDivRef.nativeElement.style.display = 'block'
		} else {
			this.licensePlateInputRef.nativeElement.classList.remove('zip-invalid')
			this.licensePlateInputRef.nativeElement.classList.add('ng-valid')
			this.errorDivRef.nativeElement.style.display = 'none'
		}
		this.errorMessageRef.nativeElement.innerText = message
	}

	validateStateInput(error: boolean, message: string) {
		if (error) {
			this.errorDivRef.nativeElement.style.display = 'block'
		} else {
			this.errorDivRef.nativeElement.style.display = 'none'
		}
		this.errorMessageRef.nativeElement.innerText = message
	}

	onLicensePlateFocus() {
		this.validateLicensePlateInput(false, '')
	}

	onStateFocus(event: any) {
		this.isStateInvalid = false

		this.validateStateInput(false, '')
	}

	onZipFocus() {
		this.validateZipInput(false, '')
	}

	preventCharacters(event: KeyboardEvent) {
		const code = event.code
		const includeKeyCodes = ['Minus', 'Space']

		if (includeKeyCodes.includes(code)) {
			event.preventDefault()
		}
	}
}
